import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Geometry, Point, LineString } from "ol/geom";
import GeoJSON from "ol/format/GeoJSON.js";
import { Circle, Fill, Text, Stroke, Style, Icon } from "ol/style";
import { CoordinatesUtils } from "../utils/coordinates-utils";
import {
  processRouteForMap,
  ProcessRouteOptions,
  routeLineStyles,
} from "../utils/map-utils";
import {
  VoyageRoute,
  VoyageRoutingModel,
} from "../models/voyage-routing-model";
import { Feature } from "ol";
import { PlanningLayersColors, STAR_ROUTE_DEVIATION_AMBER } from "./types/plan-layer.types";
import { SuggestedRouteEvents } from "../../local_modules/voyage-suggested-routes/src/types";
import { StyleFunction } from "ol/style/Style";
import { FeatureLike } from "ol/Feature";
import { SvgIcons } from "../../local_modules/voyage-svg-icons/src/voyage-svg-icons";
import { getEntryExitPoints, wpCoordKey, ZonePoint } from "../utils/zone-crossing-utils";
import { RouteEditModel } from "../models/route-edit-model";
import { isStarRouteDeviationEnabled } from "../utils/helpers";

const _PROJECTION = (<any>window)["appenv"].projection;

export class PlanningLayers extends EventTarget {
  constructor(colors?: Partial<PlanningLayersColors>) {
    super();
    this.setColors(colors || {});

    this._onRoutesChanged = this._onRoutesChanged.bind(this);
  }

  get colors(): PlanningLayersColors {
    return this._colors;
  }

  get layers(): VectorLayer<VectorSource<Geometry>>[] {
    return this._layers;
  }

  async initialize(): Promise<void> {
    this._routesVectorSource = new VectorSource();
    this._zoneMarkersVectorSource = new VectorSource();

    this._layers.push(
      new VectorLayer({ source: this._routesVectorSource, visible: true })
    );

    // The precautionary zone entry/exit markers live in their own layer so they stay visible
    // whatever else is switched on: a style zIndex only orders features inside one layer, and the
    // routes layer sits at the default 0, below the chart overlays (weather borders 10, route edit
    // 11-15, vessel track 20). 25 puts the markers above all of them - they are the one thing on
    // the route that has to be reviewed before it can be used, so it must not end up buried.
    // Both layers are handed to the map together, so the routes visibility toggle and the hover
    // info in voyage-map keep covering the markers.
    this._layers.push(
      new VectorLayer({ source: this._zoneMarkersVectorSource, visible: true, zIndex: 25 })
    );


    VoyageRoutingModel!.addEventListener(
      SuggestedRouteEvents.RoutesChanged,
      this._onRoutesChanged
    );
    VoyageRoutingModel!.addEventListener(
      "route-view-status-changed",
      this._onRoutesChanged
    );
    // Editing a route only reaches the saved geometry on save, so re-plot on every editor change to
    // keep the zone markers in step with the waypoints the user is actually looking at.
    RouteEditModel?.addEventListener("route-changed", this._onRoutesChanged);
  }

  setColors(colors: Partial<PlanningLayersColors>): void {
    this.colors.defaultColor = colors.defaultColor || this.colors.defaultColor;
    this.colors.uiRouteColors =
      colors.uiRouteColors || this.colors.uiRouteColors;
    this.colors.warningColor = colors.warningColor || this.colors.warningColor;
    this.colors.avoidColor = colors.avoidColor || this.colors.avoidColor;
    this.colors.parametricRollColor =
      colors.parametricRollColor || this.colors.parametricRollColor;
    this.colors.heavyRollingColor =
      colors.heavyRollingColor || this.colors.heavyRollingColor;
    this.colors.transverseAccelerationColor =
      colors.transverseAccelerationColor || this.colors.transverseAccelerationColor;
    this.colors.waypointFillColor =
      colors.waypointFillColor || this.colors.waypointFillColor;
    this.colors.passedColor = colors.passedColor || this.colors.passedColor;
    this.colors.heavyRollingColor =
      colors.heavyRollingColor || this.colors.heavyRollingColor;

    if (this._routesVectorSource) {
      this._onRoutesChanged();
    }
  }

  clearRoutes() {
    try {
      this._routesVectorSource!.clear();
      this._zoneMarkersVectorSource!.clear();
    } catch (error) {
      console.warn(error);
    }
  }

  private _colors: PlanningLayersColors = {
    defaultColor: "black",
    uiRouteColors: [],
    warningColor: "yellow",
    avoidColor: "red",
    waypointFillColor: "black",
    parametricRollColor: "#FE92E9",
    heavyRollingColor: "#8454D6",
    transverseAccelerationColor: "#BF6410",
    passedColor: "grey",
  };

  private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
  private _routesVectorSource: VectorSource<Geometry> | null = null;
  // The precautionary zone entry/exit markers, kept apart from the routes so they can be drawn on
  // top of every other layer.
  private _zoneMarkersVectorSource: VectorSource<Geometry> | null = null;
  // Zone point key ("lon,lat|zoneId") of the entry/exit point currently
  // highlighted (black marker).
  private _selectedZonePointKey: string = "";

  // Highlights the given entry/exit point (by zone point key) with a black
  // marker and re-plots. Pass "" to clear the highlight.
  setSelectedZonePoint(key: string) {
    if (this._selectedZonePointKey === key) return;
    this._selectedZonePointKey = key;
    this._onRoutesChanged();
  }

  /**
   * The geometry the zone markers should be derived from.
   *
   * For every route except the one open in the route editor that is simply the route's own saved
   * geometry. The edited route is different: its saved geometry only catches up on save, so a
   * waypoint the user just deleted would keep its marker on the map until then. Reading the
   * editor's live waypoints instead makes the marker disappear with the waypoint.
   */
  private _zoneGeometryFor(route: VoyageRoute, uiRouteIndex: number): any[] {
    const editedWaypoints =
      RouteEditModel && uiRouteIndex >= 0 && RouteEditModel.uiRouteIndex === uiRouteIndex
        ? RouteEditModel.getWaypoints()
        : [];

    return editedWaypoints.length > 0
      ? (editedWaypoints as any[])
      : (route.route_geometry as any[]);
  }

  private _plotRoute(route: VoyageRoute, uiRouteIndex: number) {
    const geoJSON = {
      type: "FeatureCollection",
      features: route.route_geometry,
    };

    // Entry (red) / exit (grey) no-go-zone waypoints, looked up by coordinate.
    // A waypoint can belong to more than one no-go zone (e.g. exiting one while
    // entering the next); it only carries one marker, and an entry takes
    // precedence over an exit there.
    const zoneMarkers = new Map<string, ZonePoint>();
    for (const p of getEntryExitPoints(this._zoneGeometryFor(route, uiRouteIndex))) {
      const coordKey = wpCoordKey(p.coordinates);
      const existing = zoneMarkers.get(coordKey);
      if (!existing || (existing.type === "exit" && p.type === "entry")) {
        zoneMarkers.set(coordKey, p);
      }
    }

    let uiRouteColor = this.colors.defaultColor || "black";
    if (uiRouteIndex >= 0 && this.colors.uiRouteColors[uiRouteIndex]) {
      uiRouteColor = this.colors.uiRouteColors[uiRouteIndex];
    }

    const wpHardStyle = new Style({
      image: new Circle({
        radius: 4,
        fill: new Fill({ color: this.colors.waypointFillColor.trim() }),
        stroke: new Stroke({
          color: "black",
          width: 1,
        }),
      }),
    });

    const wpNavStyle = new Style({
      text: new Text({
        text: "+",
        font: "bold 10px san-serif",
        fill: new Fill({ color: this.colors.waypointFillColor.trim() }),
        stroke: new Stroke({ color: "black", width: 1 }),
        scale: 1.8,
        offsetY: 3,
      }),
    });

    const wpPassedStyle = new Style({
      image: new Circle({
        radius: 4,
        fill: new Fill({ color: uiRouteColor.trim() }),
      }),
    });

    // No-go-zone entry/exit markers: filled circle with a white "✕".
    const zoneMarkerStyle = (fillColor: string) =>
      new Style({
        image: new Circle({
          radius: 8,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: "#FFFFFF", width: 1.5 }),
        }),
        text: new Text({
          text: "✕",
          font: "bold 11px sans-serif",
          fill: new Fill({ color: "#FFFFFF" }),
        }),
        zIndex: 30,
      });
    const entryWpStyle = zoneMarkerStyle("#E2231A");
    const exitWpStyle = zoneMarkerStyle("#878787");
    // Highlight for the selected entry/exit point: a black outline ring added
    // AROUND the marker, keeping the marker's own (red/grey) background colour.
    const selectedRingStyle = new Style({
      image: new Circle({
        radius: 8,
        stroke: new Stroke({ color: "#141414", width: 1.5 }),
      }),
      zIndex: 41,
    });

    const stoppageSvg = new Image();
        stoppageSvg.src = 'data:image/svg+xml,' + encodeURIComponent(SvgIcons.minusCircle.strings[0]);

    const pointStyleStoppage: StyleFunction = (feature: FeatureLike) => {
      return new Style({
          image: new Icon({
              img: stoppageSvg,
              imgSize: [19, 19],
              anchor: [-5, 30],
              anchorYUnits: "pixels",
              anchorXUnits: "pixels",
          }),
          zIndex: 20
      });
  };

    // Amber ring around waypoints that exceed the allowed deviation from the top-1 star route.
    const deviationRingStyle = new Style({
      image: new Circle({
        radius: 7,
        stroke: new Stroke({ color: STAR_ROUTE_DEVIATION_AMBER, width: 2 }),
      }),
      zIndex: 22,
    });

    let features = new GeoJSON().readFeatures(geoJSON);
    const KmToNM = 1 / 1.852; // Km -> Nautical miles
    const plotFeatures = [];
    // Zone markers go to their own layer, so they are collected apart from the route.
    const zoneMarkerFeatures: Feature<Geometry>[] = [];

    const processRouteOptions: ProcessRouteOptions = {
      features: features,
      addWarnings: true,
    };
    features = processRouteForMap(processRouteOptions);

    for (let feature of features) {
      if (feature.getGeometry() instanceof LineString) {
        // Transform LineString geometries
        (feature.getGeometry() as LineString).transform(
          "EPSG:4326",
          _PROJECTION
        );

        // set line style and add warnings
        feature.setStyle((feature, res) =>
          routeLineStyles(<Feature<LineString>>feature, res, {
            ...this.colors,
            uiRouteColor,
          })
        );

        const coordinates = feature
          .get("originalFeature")
          .getGeometry()
          .getCoordinates();
        const dist = CoordinatesUtils.calcDistance(
          coordinates[0],
          coordinates[1]
        );
        const routeName = this._getRoutename(route.creationTime!, uiRouteIndex);
        let name = `Route: ${routeName} Distance (leg): ${(feature.get(
          "leg_type"
        ) === "Rhumbline"
          ? dist.rl
          : dist.gc
        ).toFixed(2)}nm`;
        if (route.route_info) {
          name += ` Distance (total): ${(
            route.route_info.totals.non_eca.distance * KmToNM
          ).toFixed(2)}nm`;
        }
        feature.set("name", name);
        plotFeatures.push(feature);
      } else if (feature.getGeometry() instanceof Point) {
        // Capture the lon/lat key before projecting, to match no-go-zone markers.
        const coordKey = wpCoordKey(
          (feature.getGeometry() as Point).getCoordinates()
        );
        const zonePoint = zoneMarkers.get(coordKey);

        // Transform Point geometries
        feature.getGeometry()!.transform("EPSG:4326", _PROJECTION);

        if (zonePoint) {
          const markerStyle = zonePoint.type === "entry" ? entryWpStyle : exitWpStyle;
          // Selected point keeps its colour and gets an added black outline ring.
          feature.setStyle(
            zonePoint.key === this._selectedZonePointKey
              ? [markerStyle, selectedRingStyle]
              : markerStyle
          );
          // Tag so a map click can identify this as a no-go-zone entry/exit point.
          feature.set("zoneMarkerType", zonePoint.type);
          feature.set("zoneKey", zonePoint.key);
          feature.set("routeId", route.routeId);
        } else if (feature.get("isPassed")) {
          feature.setStyle(
            (feature.get("is_stoppage_point") === true) ? pointStyleStoppage : wpPassedStyle
          );
        } else if (feature.get("is_stoppage_point") === true) {
          feature.setStyle(pointStyleStoppage);
        } else {
          const baseStyle = feature.get("type") === "Hard" ? wpHardStyle : wpNavStyle;
          const showDeviation = isStarRouteDeviationEnabled() && feature.get("bpr_deviation_exceeds") === true;
          feature.setStyle(showDeviation ? [baseStyle, deviationRingStyle] : baseStyle);
        }
        // A zone crossing is drawn from the layer on top, every other waypoint with the route.
        (zonePoint ? zoneMarkerFeatures : plotFeatures).push(feature);
      }
    }
    this._routesVectorSource!.addFeatures(plotFeatures);
    this._zoneMarkersVectorSource!.addFeatures(zoneMarkerFeatures);
  }

  private _onRoutesChanged() {
    this.clearRoutes();
    const uiRoutes = VoyageRoutingModel!.uiRoutes;
    // Plot only enabled routes, so filter like that
    const hiddenItems = VoyageRoutingModel!._getHiddenRoutes()
    
    VoyageRoutingModel!.routes.forEach((route) => {
      if(!hiddenItems.includes(route.routeId)) {
      this._plotRoute(
        route,
        uiRoutes.findIndex((uiRoute) => uiRoute.route === route)
      );}
    });
  }

  private _getRoutename(creationTime: string, uiRouteIndex: number): string {
    let name = creationTime;
    if (uiRouteIndex >= 0) {
      name = `Route ${uiRouteIndex + 1}`;
    }
    return name;
  }
}
